import { useEffect, useMemo, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getIsLoading,
    getTrainingList,
    getTraining,
    getUserFieldsDefinition,
    getInstructorList,
    getCourseList,
    getTrainingTree,
    getSkillList,
    getCollaboratorsList,
} from "../selectors";
import {
    createTrainingDefinitionAction,
    deleteTrainingDefinitionAction,
    duplicateTrainingDefinitionAction,
    getSpecificTrainingDefinitionAction,
    getAllTrainingDefinitionAction,
    updateTrainingDefinitionAction,
    getTrainingTreeTrainingDefinitionAction,
} from "../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { RESET_ALL, RESET_SPECIFIC_TRAINING, RESET_MODULES_AND_ACTIVITIES } from "../actionTypes";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import paths from "@icarius-router/paths";

const useTrainingDefinition = () => {

    const [selectedTraining, setSelectedTraining] = useState(null);
    
    const [trainingPageIsOpen, setTrainingPageIsOpen] = useState(false);
    const [assignPageIsOpen, setAssignPageIsOpen] = useState(false);
    
    const [createTrainingFormIsOpen, setCreateTrainingFormIsOpen] = useState(false);
    const [isSkillBasedForm, setIsSkillBasedForm] = useState(false);
    const [updateTrainingFormIsOpen, setUpdateTrainingFormIsOpen] = useState(false);
    const [duplicateTrainingFormIsOpen, setDuplicateTrainingFormIsOpen] = useState(false);
    const [treeDialogIsOpen, setTreeDialogIsOpen] = useState(false);

    const [trainingInvalidFiles, setTrainingInvalidFiles] = useState(null); // para el courseFormDialog

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);

    const trainingList = useSelector(getTrainingList);
    const courseList = useSelector(getCourseList);
    const instructorList = useSelector(getInstructorList);
    const userFieldsDefinition = useSelector(getUserFieldsDefinition);
    const trainingFormData = useSelector(getTraining);
    const trainingTree = useSelector(getTrainingTree);
    const skillList = useSelector(getSkillList);
    const collaboratorsList = useSelector(getCollaboratorsList);

    useEffect(() => {
        dispatch(getAllTrainingDefinitionAction());

        return () => {
            dispatch({ type: RESET_ALL });
        }
    }, [dispatch])

    const ownColumnDef = useMemo(() => {
        const auxOwnColumnDef = getColumnDefByPage(paths.training);

        (userFieldsDefinition || []).forEach((userField) => {
            auxOwnColumnDef.push({
                headerName: userField.name,
                field: userField.name,
                filter: userField.type === "options" ? "agSetColumnFilter" : "agTextColumnFilter",
            });
        });

        return auxOwnColumnDef;
    }, [userFieldsDefinition])

    const handleOpenCreateTrainingForm = (isSkillBased = false) => {
        setIsSkillBasedForm(isSkillBased);
        setCreateTrainingFormIsOpen(true);
    }

    const handleOpenUpdateTrainingForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const trainingId = selectedRows[0].id;
        dispatch(getSpecificTrainingDefinitionAction(trainingId))
            .then((resp) => {
                if (resp.status === 200) {
                    setSelectedTraining(selectedRows[0]);
                    setUpdateTrainingFormIsOpen(true);
                }
            })
    }

    const handleCloseTrainingForm = () => {
        setSelectedTraining(null);
        setCreateTrainingFormIsOpen(false);
        setUpdateTrainingFormIsOpen(false);
        setIsSkillBasedForm(false);
        dispatch({ type: RESET_SPECIFIC_TRAINING });
    }

    const handleCreateTraining = (dataToSend) => {
        dispatch(createTrainingDefinitionAction(dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseTrainingForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setTrainingInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleUpdateTraining = (dataToSend) => {
        dispatch(updateTrainingDefinitionAction(trainingFormData.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseTrainingForm();
                } else {
                    if (resp?.data?.invalidFiles) {
                        setTrainingInvalidFiles(resp.data.invalidFiles)
                    }
                }
            });
    }

    const handleOpenDuplicateTrainingForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const trainingId = selectedRows[0].id;
        dispatch(getSpecificTrainingDefinitionAction(trainingId))
            .then((resp) => {
                if (resp.status === 200) {
                    setDuplicateTrainingFormIsOpen(true);
                }
            })
    }

    const handleCloseDuplicateTrainingForm = () => {
        setDuplicateTrainingFormIsOpen(false);
        dispatch({ type: RESET_SPECIFIC_TRAINING });
    }

    const handleDuplicateTraining = (dataToSend) => {
        dispatch(duplicateTrainingDefinitionAction(trainingFormData.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseDuplicateTrainingForm();
                }
            });
    }

    const handleOpenDeleteTrainingDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (selectedRows[0].isInUse) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("trainingInUseDelete"), severity: "warning" }));
            return;
        }

        const trainingId = selectedRows[0].id;
        dispatch(openDialogAction({
            title: getLocalizedString("attention"),
            msg: getLocalizedString("doYouWantToDeleteTraining"),
            onConfirm: () => dispatch(deleteTrainingDefinitionAction(trainingId)),
        }));
    }

    const handleOpenTrainingPage = (row) => {
        setSelectedTraining(row.data);
        setTrainingPageIsOpen(true);
    }

    const handleCloseTrainingPage = () => {
        dispatch(getAllTrainingDefinitionAction())
            .then((resp) => {
                if (resp.status === 200) {
                    setTrainingPageIsOpen(false);
                    setSelectedTraining(null);
                    dispatch({ type: RESET_MODULES_AND_ACTIVITIES });
                }
            })
    }

    const handleOpenAssignPage = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedTraining(selectedRows[0]);
        setAssignPageIsOpen(true);
    }

    const handleCloseAssignPage = () => {
        setSelectedTraining(null);
        setAssignPageIsOpen(false);
        dispatch(getAllTrainingDefinitionAction());
    }

    const handleClearInvalidFiles = () => {
        setTrainingInvalidFiles(null);
    }

    const handleOpenTreeDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        const trainingId = selectedRows[0].id;
        dispatch(getTrainingTreeTrainingDefinitionAction(trainingId))
            .then((resp) => {
                if (resp.status === 200) {
                    setTreeDialogIsOpen(true);
                }
            });
    }

    const handleCloseTreeDialog = () => {
        setTreeDialogIsOpen(false);
    }

    const state = {
        trainingPageIsOpen,
        assignPageIsOpen,
        createTrainingFormIsOpen,
        isSkillBasedForm,
        updateTrainingFormIsOpen,
        duplicateTrainingFormIsOpen,
        selectedTraining,
        trainingInvalidFiles,
        treeDialogIsOpen,
    }

    const handlers = {
        handleOpenCreateTrainingForm,
        handleOpenUpdateTrainingForm,
        handleCloseTrainingForm,
        handleCreateTraining,
        handleUpdateTraining,
        handleOpenDuplicateTrainingForm,
        handleCloseDuplicateTrainingForm,
        handleDuplicateTraining,
        handleOpenDeleteTrainingDialog,
        handleOpenTrainingPage,
        handleCloseTrainingPage,
        handleOpenAssignPage,
        handleCloseAssignPage,
        handleClearInvalidFiles,
        handleOpenTreeDialog,
        handleCloseTreeDialog,
    }

    return {
        ownColumnDef,
        trainingList,
        courseList,
        skillList,
        collaboratorsList,
        instructorList,
        userFieldsDefinition,
        trainingFormData,
        trainingTree,
        isLoading,
        state,
        handlers,
    }
}

export default useTrainingDefinition;